"use client";

import { NoxusAlert } from "@/components/UI/NoxusAlert";
import { useMemo } from "react";
import { useFormContext, useWatch, type Control } from "react-hook-form";

import {
  ComplexMarkdownField,
  AIComplexMarkdownField,
} from "@/components/Form/ComplexMarkdownField";
import { NoxusFormLabel } from "@/components/Form/NoxusFormLabel";

import { NoxusFormHelperText } from "@/components/Form/NoxusFormHelperText";
import {
  appendNodeConfigContext,
  useNodeConfigContext,
} from "@/hooks/useNodeConfigContext";
import { nodeOptionsToEditorInputs } from "./editorInputsFromOptions";
import { useUpstreamVariableOptions } from "./InlineVariablePicker";
import { isRecord } from "@/utils/isRecord";

function isVariableBindings(
  value: unknown
): value is Record<string, string | undefined> {
  return (
    isRecord(value) &&
    Object.values(value).every(
      (entry) => typeof entry === "string" || entry === undefined
    )
  );
}

/**
 * V2 renderer for `display=ConfigBindableBigText`. Multi-line markdown
 * editor where variable refs live *inline* as chips inside the markdown —
 * `:var[<ref>]` tokens in the saved value. The editor's slash menu and
 * picker drawer are wired via `ComplexMarkdownField`.
 *
 * For single-line bindable text use `BindableTextField` instead.
 */
export function BindableBigTextField({
  nodeId,
  fieldKey,
  label,
  tooltip,
  description,
  disabled,
  placeholder,
  isRequired,
  showOptionalLabel,
  error,
  enableHeuristics,
  heuristicContext,
  heuristicTypes,
  improverContext,
  improverPrompt,
  hideRawToggle,
}: {
  nodeId: string;
  fieldKey: string;
  label: string;
  tooltip?: string;
  description?: string;
  disabled?: boolean;
  placeholder?: string;
  isRequired?: boolean;
  showOptionalLabel?: boolean;
  error?: string;
  enableHeuristics?: boolean;
  heuristicContext?: string;
  heuristicTypes?: string[];
  improverContext?: string;
  improverPrompt?: string;
  // Only applies to the plain (non-heuristics) editor.
  hideRawToggle?: boolean;
}) {
  const form = useFormContext<Record<string, unknown>>();
  const path = `${nodeId}.${fieldKey}`;
  const options = useUpstreamVariableOptions({ inlineText: true });
  const inputs = useMemo(() => nodeOptionsToEditorInputs(options), [options]);
  const nodeConfigContext = useNodeConfigContext(form.control, path);
  const fullHeuristicContext = useMemo(
    () => appendNodeConfigContext(heuristicContext, nodeConfigContext),
    [heuristicContext, nodeConfigContext]
  );
  const fullImproverContext = useMemo(
    () => appendNodeConfigContext(improverContext, nodeConfigContext),
    [improverContext, nodeConfigContext]
  );
  const variableBindings = useWatch({
    control: form.control,
    name: `${nodeId}.variableBindings`,
  });
  const typedVariableBindings = isVariableBindings(variableBindings)
    ? variableBindings
    : undefined;
  const resolvedLabel = useMemo(
    () => label || humanize(fieldKey),
    [label, fieldKey]
  );
  const invalid = !!error;

  return (
    <div className="flex flex-col gap-1">
      <NoxusFormLabel
        label={resolvedLabel}
        tooltip={tooltip}
        name={fieldKey}
        noMargin
        isDisabled={disabled}
        isRequired={isRequired}
        showOptionalLabel={showOptionalLabel}
      />
      {enableHeuristics ? (
        <AIComplexMarkdownField
          control={form.control as Control<Record<string, unknown>>}
          name={path}
          dialogTitle={resolvedLabel}
          placeholder={placeholder}
          isDisabled={disabled}
          inputs={inputs}
          invalid={invalid}
          nodeId={nodeId}
          variableBindings={typedVariableBindings}
          enableHeuristics={enableHeuristics}
          heuristicContext={fullHeuristicContext}
          heuristicTypes={heuristicTypes}
          improverContext={fullImproverContext}
          improverPrompt={improverPrompt}
          includeNodeConfigContext={false}
        />
      ) : (
        <ComplexMarkdownField
          control={form.control as Control<Record<string, unknown>>}
          name={path}
          placeholder={placeholder}
          isDisabled={disabled}
          inputs={inputs}
          invalid={invalid}
          nodeId={nodeId}
          variableBindings={typedVariableBindings}
          hideRawToggle={hideRawToggle}
        />
      )}
      {description && !error ? (
        <NoxusFormHelperText>{description}</NoxusFormHelperText>
      ) : null}
      {error ? (
        <NoxusAlert look="banner" title={error} variant="warning" size="xs" />
      ) : null}
    </div>
  );
}

function humanize(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}
